/**
 * @voltxampere/web — Always-live mode state (Sprint 33-1)
 *
 * Vision §E "kullanıcı F5'e basmak zorunda kalmadan canlı görür"
 * hedefinin son parçası. Devre değiştiğinde otomatik transient
 * re-run, 250 ms debounce ile spam'i sönümler.
 *
 * Architecture (Sprint 30 lesson — verified shapes):
 *   - `currentCircuit: signal<Circuit | null>` — apps/web/src/store/circuit.ts
 *   - `runTransient(circuit, opts?)` — apps/web/src/store/transient-runner.ts,
 *     takes explicit circuit (NOT zero-arg)
 *
 * The auto-rerun effect lives here (not inside transient-runner.ts) so
 * `transient-runner` stays a pure imperative API surface that tests
 * can call without spawning the always-live watcher. Boot wires it
 * via `installAlwaysLiveAutoRerun()` from `apps/web/src/main.ts`.
 *
 * Defaults: `alwaysLiveEnabled = true` per Sprint 33 prompt §G1.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { effect, signal } from "@preact/signals-core";

import { currentCircuit } from "./circuit.js";
import { runTransient } from "./transient-runner.js";

/** Debounce window for circuit-change → re-run (ms). */
export const ALWAYS_LIVE_DEBOUNCE_MS = 250;

/**
 * Master toggle. `true` = circuit edits auto-trigger a debounced
 * transient re-run. `false` = legacy behaviour (Run button / F5
 * required).
 *
 * Persistence is wired in 33-4 via the `SettingsRecord.liveMode`
 * additive field; the boot path hydrates this signal from IDB.
 */
export const alwaysLiveEnabledSignal = signal<boolean>(true);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let installed = false;
let watcherDisposer: (() => void) | null = null;

/**
 * Install the auto-rerun watcher. Idempotent — calling twice is a
 * no-op (logs a dev warning so duplicate boots surface).
 *
 * Returns a disposer for tests; production never calls it.
 */
export function installAlwaysLiveAutoRerun(): () => void {
  if (installed) {
    if (import.meta.env.DEV) {
      console.warn("[VXA always-live] installAlwaysLiveAutoRerun called twice; ignoring");
    }
    return () => {
      /* no-op for the duplicate caller */
    };
  }
  installed = true;
  watcherDisposer = effect(() => {
    // Subscribe to BOTH signals so toggling on/off re-evaluates.
    const enabled = alwaysLiveEnabledSignal.value;
    const circuit = currentCircuit.value;

    // Always clear any pending timer when state changes — prevents
    // a stale rerun from firing after the user disables always-live
    // or after a more recent edit lands.
    if (debounceTimer !== null) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    if (!enabled) return;
    if (circuit === null) return;

    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      // Re-read the signal at fire time — the user may have flipped
      // the toggle off between the schedule and the fire.
      if (!alwaysLiveEnabledSignal.value) return;
      const fresh = currentCircuit.value;
      if (fresh === null) return;
      void runTransient(fresh);
    }, ALWAYS_LIVE_DEBOUNCE_MS);
  });
  return disposeAlwaysLiveAutoRerun;
}

/**
 * Tear down the watcher. Production never calls this; tests use it
 * to start fresh between cases.
 */
export function disposeAlwaysLiveAutoRerun(): void {
  if (debounceTimer !== null) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
  if (watcherDisposer !== null) {
    watcherDisposer();
    watcherDisposer = null;
  }
  installed = false;
}

/** Test-only — reset signal + watcher state. */
export function __resetAlwaysLiveForTests(): void {
  disposeAlwaysLiveAutoRerun();
  alwaysLiveEnabledSignal.value = true;
}
